import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { size } from 'lodash';

import {
  Button,
  Dialog,
  DialogBody,
  DialogHeader,
  FileUpload,
  Icon,
  Skeleton,
  SkeletonTable,
  Tooltip,
  DialogFooter,
} from '@repo/ui';
import {
  useDeleteMatterInvoiceSummaryFile,
  useMatterInvoiceSummaryFilesQuery,
  usePreviewFileQuery,
  useUploadMatterInvoiceSummaryFiles,
} from '../../queries/index.js';
import { DataTable, DataTableColumn } from '@xtnd/web/shared/components';
import { FileCategory, MatterInvoiceSummaryFile } from '../../types/index.js';
import { AR_ATTACHMENT_ACCEPT, AR_ATTACHMENT_MAX_SIZE } from '../../constants/attachments.js';
import { dateUtil, fileUtil } from '@xtnd/web/shared';
import { DateFormat } from '@xtnd/web/shared/utils/format.js';

interface DocumentLibraryModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  invoiceId: string;
  maxFileNumber?: number;
  onClose: () => void;
}

interface DocumentDeleteConfirmDialogProps {
  isOpen: boolean;
  filename: string;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

const DocumentDeleteConfirmDialog = ({
  isOpen,
  onClose,
  filename,
  onConfirm,
  isLoading,
}: Readonly<DocumentDeleteConfirmDialogProps>) => {
  return (
    <Dialog open={isOpen} onClose={onClose} size="lg">
      <DialogHeader>
        <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
          Delete Document
        </h3>
      </DialogHeader>
      <DialogBody className="!px-4 text-sm">
        <p className="text-xtnd-dark dark:text-xtnd-light mb-4">
          Are you sure you want to delete <strong>{filename}</strong>?
        </p>
        <DialogFooter className="!px-0">
          <Button variant="outline" className="text-sm" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} loading={isLoading}>
            Delete
          </Button>
        </DialogFooter>
      </DialogBody>
    </Dialog>
  );
};

/**
 * Modal wrapper around the shared FileUpload component.
 * Collects supporting files for an appeal workflow and hands them back to the caller.
 */
export const DocumentLibraryModal: React.FC<DocumentLibraryModalProps> = ({
  isOpen,
  invoiceId,
  onClose,
  maxFileNumber = 12,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const {
    data: response,
    isFetching: isFetchingGetFiles,
    isLoading: isLoadingGetFiles,
    error,
  } = useMatterInvoiceSummaryFilesQuery(invoiceId, { enabled: isOpen });
  const { mutateAsync: uploadFiles, isPending: isUploadingFiles } =
    useUploadMatterInvoiceSummaryFiles(invoiceId, 'AR Collections', {
      category: FileCategory.PAYMENT_SUPPORT,
    });

  const [selectedDoc, setSelectedDoc] = useState<MatterInvoiceSummaryFile | null>(null);

  const { mutateAsync: deleteDocument, isPending: isDeletingDocument } =
    useDeleteMatterInvoiceSummaryFile(invoiceId);
  const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(false);
  const [action, setAction] = useState<string | null>(null);

  const { isLoading: isDocLoading, data: document } = usePreviewFileQuery(
    selectedDoc?.fileId || '',
    { enabled: action === 'view' && true }
  );

  useEffect(() => {
    if (document?.data && action == 'view') {
      window.open(document.data, '_blank');
      setSelectedDoc(null);
    }
  }, [document, action]);

  const renderActionCell = useCallback(
    (file: MatterInvoiceSummaryFile) => {
      if (isDocLoading && selectedDoc?.fileId === file.fileId) {
        return (
          <Icon
            name="FaSpinner"
            size="sm"
            className="cursor-pointer animate-spin text-xtnd-dark hover:text-xtnd-blue dark:text-xtnd-light"
          />
        );
      }
      return (
        <div className="flex gap-x-2">
          <Icon
            name="HiEye"
            title="Preview document"
            size="sm"
            className="cursor-pointer text-xtnd-dark hover:text-xtnd-blue dark:text-xtnd-light"
            onClick={() => {
              setSelectedDoc(file);
              setAction('view');
            }}
          />
          <Icon
            name="FaTrash"
            title="Delete document"
            size="sm"
            className="!h-4 !w-4 text-red-500 hover:text-red-500/50 cursor-pointer"
            onClick={() => {
              setAction('delete');
              setSelectedDoc(file);
              setIsDeleteConfirmDialogOpen(true);
            }}
          />
        </div>
      );
    },
    [isDocLoading, selectedDoc, setSelectedDoc]
  );

  const columns: DataTableColumn<MatterInvoiceSummaryFile>[] = useMemo(
    () => [
      {
        key: 'fileName',
        header: 'File Name',
        renderCell: _file => (
          <Tooltip
            content={_file.fileName ?? ''}
            position="auto"
            className="rounded-md"
            contentClassName="min-w-[12rem] h-full"
          >
            <span className="text-sm text-gray-900 dark:text-gray-100 block">
              <div className="text-sm text-xtnd-dark dark:text-xtnd-light max-w-[12rem] truncate block">
                {_file.fileName}
              </div>
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'size',
        header: 'Size',
        defaultWidth: 100,
        renderCell: _file => (
          <div className="text-sm text-xtnd-dark dark:text-xtnd-light w-[80px]">
            {fileUtil.formatFileSize(_file.size)}
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: 'Created At',
        renderCell: _file => (
          <div className="text-sm text-xtnd-dark dark:text-xtnd-light">
            {dateUtil.formatDate(_file.createdAt, DateFormat.US_WITH_TIME)}
          </div>
        ),
      },
      {
        key: 'action',
        header: 'Action',
        renderCell: _file => (
          <div className="text-sm text-xtnd-dark dark:text-xtnd-light flex justify-center gap-3">
            {renderActionCell(_file)}
          </div>
        ),
      },
    ],
    [renderActionCell]
  );

  const handleFilesChange = useCallback((nextFiles: File[]) => {
    setFiles(nextFiles);
  }, []);

  const handleClose = useCallback(() => {
    setFiles([]);
    setAction(null);
    setSelectedDoc(null);
    onClose();
  }, [onClose]);

  const handleUploadFiles = useCallback(async () => {
    if (size(files) === 0 || size(files) > maxFileNumber) return;

    await uploadFiles({
      files,
      successCallback: () => {
        setFiles([]);
      },
    });
  }, [files, maxFileNumber, uploadFiles]);

  const handleDeleteDocument = useCallback(async () => {
    if (!selectedDoc) return;
    await deleteDocument({
      fileId: selectedDoc.fileId,
      successCallback: () => {
        setSelectedDoc(null);
        setIsDeleteConfirmDialogOpen(false);
      },
    });
  }, [deleteDocument, selectedDoc]);

  const renderBodyContent = useCallback(() => {
    if (isLoadingGetFiles) {
      return (
        <div className="space-y-4 mb-2">
          <div className="space-y-2">
            <Skeleton variant="text" width="30%" height="1rem" />
            <Skeleton variant="text" width="55%" height="0.875rem" />
          </div>

          <Skeleton
            variant="rectangular"
            height="200px"
            className="border border-dashed border-gray-200 dark:border-gray-700 rounded-lg"
          />

          <div className="space-y-1">
            <Skeleton variant="text" width="50%" height="0.75rem" />
            <Skeleton variant="text" width="40%" height="0.75rem" />
          </div>

          <div className="flex justify-end">
            <Skeleton variant="rectangular" width="96px" height="36px" className="rounded-md" />
          </div>

          <div className="space-y-3">
            <Skeleton variant="text" width="25%" height="1rem" />
            <SkeletonTable rows={4} columns={4} />
          </div>
        </div>
      );
    }
    
const paymentSupportFiles =
  response?.data?.filter(file => file.category === FileCategory.PAYMENT_SUPPORT) || [];
const appealsSupportFiles =
      response?.data?.filter(file => file.category === FileCategory.APPEALS_SUPPORT) || [];
    
return (
  <div className="space-y-2 mb-2">
    <p className=" text-sm font-medium text-xtnd-dark dark:text-xtnd-light mb-1 flex flex-col">
      <span>Attachments</span>
      <span className="text-xs text-gray-400 dark:text-gray-200 italic font-normal">
        Please include the original BAR and the appeal BAR.
      </span>
    </p>
    <FileUpload
      files={files}
      isMultiple={true}
      disabled={isUploadingFiles}
      accept={AR_ATTACHMENT_ACCEPT}
      maxSize={AR_ATTACHMENT_MAX_SIZE}
      onChange={handleFilesChange}
      isDisplayUploadButton={false}
      maxFileNumber={maxFileNumber}
    />

    <div className="flex justify-end mt-4">
      <Button
        variant="primary"
        leftIcon={<Icon name="HiUpload" size="sm" />}
        size="sm"
        disabled={size(files) == 0 || size(files) > maxFileNumber}
        className="rounded-none"
        loading={isUploadingFiles}
        onClick={handleUploadFiles}
      >
        Upload
      </Button>
    </div>

    <div className="mt-4 space-y-4">
      <div>
        <p className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light mb-2">
          Payment Support
        </p>
        {paymentSupportFiles.length > 0 ? (
          <DataTable
            columns={columns}
            data={{
              data: paymentSupportFiles,
              total: paymentSupportFiles.length || 0,
            }}
            isLoading={isFetchingGetFiles}
            rowKey="fileId"
            stickyHeader={true}
            error={error}
            emptyState={{
              icon: 'HiDocumentReport',
              title: 'No documents available',
              description: 'There are no documents matching your current filters.',
            }}
          />
        ) : (
          <div className="text-sm text-xtnd-dark dark:text-xtnd-light border border-gray-200 dark:border-gray-700 rounded-lg p-4 justify-center">
            <div className="flex flex-col items-center justify-center gap-2">
              <Icon
                name="HiDocumentReport"
                size="sm"
                className="text-gray-500 dark:text-xtnd-light"
              />
              <p className="text-sm text-gray-500 dark:text-xtnd-light">No documents available</p>
            </div>
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light mb-2">
          Appeals Support
        </p>
        {appealsSupportFiles.length > 0 ? (
          <DataTable
            columns={columns}
            data={{
              data: appealsSupportFiles,
              total: appealsSupportFiles.length || 0,
            }}
            isLoading={isFetchingGetFiles}
            rowKey="fileId"
            stickyHeader={true}
            error={error}
            emptyState={{
              icon: 'HiDocumentReport',
              title: 'No documents available',
              description: 'There are no documents matching your current filters.',
            }}
          />
        ) : (
          <div className="text-sm text-xtnd-dark dark:text-xtnd-light border border-gray-200 dark:border-gray-700 rounded-lg p-4 justify-center">
            <div className="flex flex-col items-center justify-center gap-2">
              <Icon
                name="HiDocumentReport"
                size="sm"
                className="text-gray-500 dark:text-xtnd-light"
              />
              <p className="text-sm text-gray-500 dark:text-xtnd-light">No documents available</p>
            </div>
          </div>
        )}
      </div>
      <DocumentDeleteConfirmDialog
        isOpen={isDeleteConfirmDialogOpen}
        filename={selectedDoc?.fileName || ''}
        onClose={() => {
          if (isDeletingDocument) {
            return;
          }
          setIsDeleteConfirmDialogOpen(false);
          setSelectedDoc(null);
        }}
        onConfirm={handleDeleteDocument}
        isLoading={isDeletingDocument}
      />
    </div>
  </div>
);
  }, [
    isLoadingGetFiles,
    files,
    isUploadingFiles,
    handleFilesChange,
    maxFileNumber,
    handleUploadFiles,
    columns,
    response?.data,
    isFetchingGetFiles,
    error,
    isDeleteConfirmDialogOpen,
    selectedDoc?.fileName,
    handleDeleteDocument,
    isDeletingDocument,
  ]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      size="xxl"
      showCloseButton={false}
      className="!max-h-[90vh] overflow-y-auto"
    >
      <DialogHeader className="sticky top-0 bg-white dark:bg-xtnd-dark-800 z-[51]">
        <div className="flex justify-between">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
            Billing Analysis Report (BAR)
          </h3>
          <button
            type="button"
            className="absolute z-[52] top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700"
            onClick={handleClose}
            aria-label="Close dialog"
          >
            <Icon name="HiOutlineX" />
          </button>
        </div>
      </DialogHeader>

      <DialogBody className="space-y-4">{renderBodyContent()}</DialogBody>
    </Dialog>
  );
};

export default DocumentLibraryModal;
